import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import FormTownHall, { TownHallForm } from 'system/components/town-halls/form';
import { TownHall } from 'shell/models/town-hall';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonTownHallCrumbs } from '../constants';
import { CirculationArea } from 'shell/models/circulation-area';
import { toast } from 'react-toastify';

const TownHallEditPage = (): React.ReactElement => {
  const params = useParams();
  const {
    put: updateTownHall,
    data,
    get: getTownHall,
    loading,
    error,
  } = useHttpFetch<HttpResponse<TownHall>>(`${__TOPONYMY_API_URL__}/municipi/${params.id}`, {
    data: {},
  });

  const {
    get: getCirculationArea,
    data: circulationArea,
    loading: loadingArea,
  } = useHttpFetch<HttpResponse<CirculationArea>>(`${__TOPONYMY_API_URL__}/areaCircolazione`);

  const townHallInitialValues: TownHallForm = useMemo(() => {
    const townhall = data?.payload;
    if (!townhall || !!error) {
      return {
        code: '',
        description: '',
        streetNumber: '',
        shortDescription: '',
        address: null,
        civicRepresentative: '',
      };
    }
    return {
      address: {
        code: circulationArea?.payload.key?.codice || 0,
        description: circulationArea?.payload.descrizionePostale || '',
      },
      shortDescription: townhall.descrizioneBreve,
      streetNumber: townhall.numeroCivico,
      code: townhall.key?.codice,
      description: townhall.descrizione,
      civicRepresentative: townhall.esponenteCivico,
    };
  }, [data, circulationArea]);

  const navigate = useNavigate();
  const onSubmit = useCallback(
    (formValues) => {
      updateTownHall({
        descrizioneBreve: formValues.shortDescription,
        descrizione: formValues.description,
        numeroCivico: formValues.streetNumber,
        areaCircolazione: formValues.address?.code,
        esponenteCivico: formValues.civicRepresentative,
        key: {
          codice: formValues.code,
        },
      }).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sistema/municipi');
      });
    },
    [updateTownHall, navigate]
  );

  useEffect(() => {
    getTownHall().then((response) => {
      const serviceResponse = response as HttpResponse<TownHall>;
      getCirculationArea(serviceResponse.payload.areaCircolazione?.toString());
    });
  }, [params.id]);

  const loadingService = loading && !data && loadingArea && !circulationArea;

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonTownHallCrumbs, { name: 'Modifica Municipio' }]} />
      {loadingService ? null : (
        <FormTownHall
          title="Modifica Municipio"
          onOk={onSubmit}
          onCancel={() => {
            navigate('/sistema/municipi');
          }}
          isEdit
          initialValues={townHallInitialValues}
        />
      )}
    </Fragment>
  );
};

TownHallEditPage.displayName = 'Town Hall Edit page component';

export default TownHallEditPage;
